import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  addProcessIntermediateNoDataToProduct,
  getProcessIntermediateNoDataList,
  updateProcessIntermediateNoData,
  deleteProcessIntermediateNoData,
} from "../../../../../../apis/Product/ProcessIntermediateSoldNoDataApi";
import {
  // getProcessingIntermediateDropdown,
  getTransportCountryData,
} from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { useBonsaiData } from "../../../../../../hooks/useBonsaiData";

const ProcessingNoDataTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownCountry, setDropdownCountry] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  // Use cached Bonsai data instead of individual API call
  const { data: dropdownProcess, isLoading: isBonsaiLoading } = useBonsaiData();

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getProcessIntermediateNoDataList({
      productId,
      setObjects: setData,
    });
    getTransportCountryData({ setDropdownData: setDropdownCountry });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0 && dropdownProcess?.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["manufacturing_process_emit"]?.value ||
          row.manufacturing_process_emit;

        if (materialValue) {
          const selectedMaterial = dropdownProcess.find(
            (option) =>
              option?.dropdown === materialValue ||
              option?.dropdown_en === materialValue
          );

          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownProcess]);

  const dynamicPlaceholders = useMemo(
    () => ({
      manufacturing_process_emit: {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedItem = dropdownData.find(
            (item) =>
              item.dropdown === selectedValue ||
              item.dropdown_en === selectedValue
          );
          if (selectedItem) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedItem.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedItem.unit,
              }));
            }
          }
        },
        dropdownDataKey: "manufacturing_process_emit",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const foundProduct = dropdownProcess?.find(
        (option) => option.name === editedItem.manufacturing_process_emit.value
      );
      const product_code = foundProduct
        ? foundProduct["code"]
        : editedItem.product_code;
      updateProcessIntermediateNoData({
        productId,
        componentId: editedItem.id,
        ...editedItem,
        manufacturing_process_emit: editedItem.manufacturing_process_emit.value,
        country: editedItem.country.value,
        objects: data,
        setObjects: setData,
        use_swal: true,
        product_code: product_code,
      });
    },
    [productId, data, setData, dropdownProcess]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const foundProduct = dropdownProcess?.find(
        (option) => option.name === newItem.manufacturing_process_emit.value
      );

      const product_code = foundProduct ? foundProduct["code"] : "";
      addProcessIntermediateNoDataToProduct({
        productId,
        ...newItem,
        manufacturing_process_emit: newItem.manufacturing_process_emit.value,
        country: newItem.country.value,
        callback: fetchData,
        use_swal: true,
        product_code: product_code,
      });
    },
    [productId, fetchData, dropdownProcess]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProcessIntermediateNoData({
        productId,
        componentId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "country",
        header: `${t(
          "In which country are your functional unit further processed?"
        )}`,
        type: "dropdown",
        dropdownData: dropdownCountry,
        disableSortBy: true,
        width: "130px",
        tipHeader: "Tip",
        tipBody: t("COUNTRY_PROCESSING_TIP"),
      },
      {
        accessor: "manufacturing_process_emit",
        header: `${t("Which manufacturing processes emit CO2?")}`,
        type: "dropdown",
        dropdownData: dropdownProcess,
        disableSortBy: true,
        width: "130px",
        tipHeader: "Tip",
        tipBody: t("manufacturing_process_emit_NO_DATA_TIP"),
      },
      {
        accessor: "qty",
        header: `${t(
          "How much of the functional unit is processed in this country?"
        )}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        unit: (rowId) => `${unitMaterials[rowId] || "Unit"}`,
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "90px",
      },
    ],
    [dropdownProcess, dropdownCountry, unitMaterials, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      manufacturing_process_emit: dropdownProcess,
      country: dropdownCountry,
    }),
    [dropdownProcess, dropdownCountry]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default ProcessingNoDataTable;
